import { Component, output, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

export type UploadMode = 'default' | 'culture' | 'pdf';

@Component({
  selector: 'app-upload-files',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatProgressBarModule,
    MatSnackBarModule,
  ],
  templateUrl: './upload-files.component.html',
  styleUrl: './upload-files.component.css',
})
export class UploadFilesComponent implements OnInit {
  // Input to specify the mode (default or culture)
  @Input() mode: UploadMode = 'default';

  // Add singleFile input property
  @Input() singleFile: boolean = false;

  // Add new inputs for aspect ratio validation
  @Input() validateAspectRatio: boolean = false;
  @Input() requiredWidth: number = 0;
  @Input() requiredHeight: number = 0;
  @Input() requiredAspectRatio: number = 0; // e.g. 9/16 for portrait mode

  // Replace @Output() EventEmitter with signal-based output()
  filesSelected = output<File[]>();

  files: File[] = [];
  isDragging = false;

  // Default validation settings
  maxFileSize = 1024 * 1024 * 1024; // 20MB in bytes
  allowedFileTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'];
  acceptedExtensions = '.png,.jpg,.jpeg,.gif';
  fileRestrictions =
    'Tipo de formato permitido: PNG, JPG y GIF. Tamaño máximo por archivo: 20MB.';

  constructor(private snackBar: MatSnackBar) {}

  ngOnInit(): void {
    // Configure validation rules based on mode
    if (this.mode === 'culture') {
      this.allowedFileTypes = [
        // Images
        'image/png',
        'image/jpeg',
        'image/jpg',
        'image/gif',
        // Videos
        'video/mp4',
        'video/avi',
        'video/quicktime',
        'video/x-ms-wmv',
      ];
      this.maxFileSize = 1024 * 1024 * 1024; // 50MB for videos
      this.acceptedExtensions = '.png,.jpg,.jpeg,.gif,.mp4,.avi,.mov,.wmv';
      this.fileRestrictions =
        'Imágenes: PNG, JPG y GIF (máx. 20MB). Videos: MP4, AVI, MOV, WMV (máx. 50MB).';
    } else if (this.mode === 'pdf') {
      this.allowedFileTypes = ['application/pdf'];
      this.maxFileSize = 1024 * 1024 * 1024; // 20MB
      this.acceptedExtensions = '.pdf';
      this.fileRestrictions = 'Solo archivos PDF. Tamaño máximo: 20MB';
    }
  }

  onFileDropped(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;

    if (event.dataTransfer?.files) {
      this.handleFiles(event.dataTransfer.files);
    }
  }

  onFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      this.handleFiles(input.files);
    }
  }

  handleFiles(fileList: FileList): void {
    const filesArray = Array.from(fileList);
    const validFiles: File[] = [];
    const validationPromises: Promise<boolean>[] = [];

    for (const file of filesArray) {
      // Check file type
      if (!this.allowedFileTypes.includes(file.type)) {
        this.snackBar.open(
          `${file.name} no es un tipo de archivo válido. Revise los formatos permitidos.`,
          'Cerrar',
          { duration: 3000 }
        );
        continue;
      }

      // Check file size based on type
      const isVideo = file.type.startsWith('video/');
      const maxSize = isVideo ? 5120 * 1024 * 1024 : 1024 * 1024 * 1024;

      if (file.size > maxSize) {
        const sizeLimit = isVideo ? '5GB' : '1GB';
        this.snackBar.open(
          `${file.name} excede el tamaño máximo de ${sizeLimit}.`,
          'Cerrar',
          { duration: 3000 }
        );
        continue;
      }

      // If aspect ratio validation is enabled and it's an image, check dimensions
      if (this.validateAspectRatio && file.type.startsWith('image/')) {
        const promise = this.validateImageDimensions(file).then((isValid) => {
          if (isValid) {
            validFiles.push(file);
          }
          return isValid;
        });
        validationPromises.push(promise);
      } else {
        validFiles.push(file);
      }
    }

    // If we have promises to resolve for image validation
    if (validationPromises.length > 0) {
      Promise.all(validationPromises).then(() => {
        this.updateFiles(validFiles);
      });
    } else {
      this.updateFiles(validFiles);
    }
  }

  private updateFiles(validFiles: File[]): void {
    // If singleFile is true, replace existing files with the new one
    if (this.singleFile) {
      this.files = validFiles.length > 0 ? [validFiles[0]] : [];
    } else {
      this.files = [...this.files, ...validFiles];
    }

    // Update to use emit() method on the output signal
    this.filesSelected.emit(this.files);
  }

  // Add a method to validate image dimensions
  private validateImageDimensions(file: File): Promise<boolean> {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src);

        // Check aspect ratio if specified
        if (this.requiredAspectRatio > 0) {
          const aspectRatio = img.width / img.height;
          const tolerance = 10; // Allow 1% tolerance in aspect ratio

          if (Math.abs(aspectRatio - this.requiredAspectRatio) > tolerance) {
            this.snackBar.open(
              `La imagen ${file.name} debe tener una proporción de aspecto de 9:16.`,
              'Cerrar',
              { duration: 5000 }
            );
            resolve(false);
            return;
          }
        }

        resolve(true);
      };

      img.onerror = () => {
        URL.revokeObjectURL(img.src);
        this.snackBar.open(
          `No se pudo cargar la imagen ${file.name} para validación.`,
          'Cerrar',
          { duration: 3000 }
        );
        resolve(false);
      };

      img.src = URL.createObjectURL(file);
    });
  }

  removeFile(index: number): void {
    this.files.splice(index, 1);
    this.files = [...this.files]; // Create new array reference
    this.filesSelected.emit(this.files);
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    this.isDragging = false;
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return bytes + ' bytes';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  }

  clearFiles(): void {
    this.files = [];
    this.filesSelected.emit(this.files);
  }

  getFileIcon(file: File): string {
    if (file.type.startsWith('image/')) {
      return 'image';
    } else if (file.type.startsWith('video/')) {
      return 'videocam';
    } else if (file.type === 'application/pdf') {
      return 'picture_as_pdf';
    }
    return 'insert_drive_file';
  }
}
